import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router";
import {
  Badge,
  Button,
  Card,
  EmptyState,
  PageHeader,
  SearchInput,
  Spinner,
  Stat,
  Table,
  type BadgeVariant,
  type TableColumn,
} from "../ds";
import { api, errorText, type Job } from "../lib/api";
import { fmtDateTime, relTime } from "../lib/format";

// Audit: Historie aller Kommandos, die der Server an Router geschickt hat —
// wer, wann, was, mit welchem Ergebnis. Quelle: GET /api/jobs[?device=…].

const STATUS_VARIANT: Record<Job["status"], BadgeVariant> = {
  pending: "neutral",
  running: "info",
  ok: "success",
  error: "danger",
};

function jobColumns(onDeviceClick: (id: string) => void): TableColumn<Job>[] {
  return [
    {
      key: "time",
      header: "Zeit",
      cell: (j) => (
        <span title={fmtDateTime(j.createdAt)}>
          {fmtDateTime(j.createdAt)}
          <span className="block text-xs text-content-3">{relTime(j.createdAt)}</span>
        </span>
      ),
    },
    {
      key: "device",
      header: "Router",
      cell: (j) =>
        j.deviceId ? (
          <Button size="sm" variant="ghost" onClick={() => onDeviceClick(j.deviceId ?? "")}>
            {j.deviceHostname ?? j.deviceId}
          </Button>
        ) : (
          "—"
        ),
    },
    { key: "action", header: "Aktion", mono: true, cell: (j) => j.action },
    {
      key: "status",
      header: "Status",
      cell: (j) => (
        <Badge variant={STATUS_VARIANT[j.status] ?? "neutral"} dot>
          {j.status}
        </Badge>
      ),
    },
    { key: "actor", header: "Ausgelöst von", hideOnMobile: true, cell: (j) => j.actor ?? "—" },
    {
      key: "error",
      header: "Fehler",
      // Fehlertexte sind Fließtext — als einzige Spalte umbrechen lassen.
      wrap: true,
      cell: (j) => <span className="text-xs text-danger-fg">{j.error ?? ""}</span>,
    },
  ];
}

export function AuditPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const deviceFilter = searchParams.get("device") ?? "";
  const [loaded, setLoaded] = useState<{ filter: string; jobs: Job[] } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState("");

  // Das Ergebnis trägt den Filter, zu dem es gehört. Liegt ein anderer an, ist
  // `jobs` null und die Seite lädt sichtbar — ohne dass der Effekt dafür
  // Zustand zurücksetzen muss (das wäre ein Renderlauf mehr und die Zeilen des
  // vorigen Routers wären für einen Frame als gefiltert zu sehen).
  const jobs = loaded?.filter === deviceFilter ? loaded.jobs : null;

  const load = useCallback(() => {
    api
      .listJobs(deviceFilter || undefined)
      .then((j) => {
        setLoaded({ filter: deviceFilter, jobs: j });
        setError(null);
      })
      .catch((e: unknown) => {
        setLoaded({ filter: deviceFilter, jobs: [] });
        setError(errorText(e));
      });
  }, [deviceFilter]);

  useEffect(load, [load]);

  const setDevice = (id: string) => {
    const next = new URLSearchParams(searchParams.toString());
    if (id) next.set("device", id);
    else next.delete("device");
    setSearchParams(next, { replace: true });
  };

  const rows = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q || !jobs) return (jobs ?? []).slice();
    return jobs.filter((j) =>
      [j.action, j.actor, j.status, j.deviceHostname, j.deviceId, j.error]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q)),
    );
  }, [jobs, query]);

  const failed = jobs?.filter((j) => j.status === "error").length ?? 0;

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="Audit"
        subtitle="Jedes Kommando an einen Router wird protokolliert — inklusive Ergebnis."
        actions={
          <Button variant="outline" iconName="refresh" onClick={load}>
            Neu laden
          </Button>
        }
      />

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Stat label="Einträge" value={jobs?.length ?? 0} iconName="log" loading={jobs === null} />
        <Stat label="Fehlgeschlagen" value={failed} iconName="close" loading={jobs === null} />
        <Stat
          label="Letzter Eintrag"
          value={jobs?.[0] ? relTime(jobs[0].createdAt) : "—"}
          iconName="timer"
          loading={jobs === null}
        />
        <Stat label="Filter" value={deviceFilter || "alle Router"} iconName="filter" />
      </div>

      <Card
        title="Kommando-Historie"
        subtitle={deviceFilter ? `gefiltert auf ${deviceFilter}` : "alle Router"}
        actions={
          <>
            <SearchInput value={query} onChange={setQuery} placeholder="Aktion, Person, Fehler …" />
            {deviceFilter && (
              <Button size="sm" variant="ghost" iconName="close" onClick={() => setDevice("")}>
                Filter entfernen
              </Button>
            )}
          </>
        }
        bodyPadding={false}
      >
        {jobs === null ? (
          <div className="flex justify-center p-10">
            <Spinner />
          </div>
        ) : error ? (
          <EmptyState iconName="close" title="Historie nicht ladbar" description={error} />
        ) : (
          <Table
            columns={jobColumns(setDevice)}
            rows={rows}
            rowKey={(j) => j.id}
            emptyMessage="Keine Einträge — hier erscheint jedes ausgelöste Kommando."
          />
        )}
      </Card>
    </div>
  );
}
